import React, { useState, useEffect } from 'react';
import { authFetch } from '../../../utils/authFetch';
import { useUser } from '../../../context/UserContext';

interface RiskProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning';
  reasonings?: string[];
  organizationId?: string;
}

const RiskProfileModal: React.FC<RiskProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  reasonings = [],
  organizationId
}) => {
  const { accessToken, setUserAndToken } = useUser();
  const [categoryDetails, setCategoryDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [categoryCache, setCategoryCache] = useState<Record<string, any>>({});

  // Fetch category details when modal opens
  useEffect(() => {
    if (!isOpen || !profile || !organizationId || !accessToken) {
      setCategoryDetails(null);
      return;
    }

    // Check cache first
    if (categoryCache[profile]) {
      setCategoryDetails(categoryCache[profile]);
      return;
    }

    // Fetch from API
    const fetchCategoryDetails = async () => {
      setLoading(true);
      try {
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/customer-categories`,
          {},
          accessToken,
          setUserAndToken
        );

        if (response.ok) {
          const responseData = await response.json();
          const categories = responseData.data;
          
          // Find the specific category by name
          const categoryData = categories.find((cat: any) => cat.name === profile);
          
          // Cache the data
          setCategoryCache(prev => ({
            ...prev,
            [profile]: categoryData
          }));
          
          setCategoryDetails(categoryData);
        } else {
          setCategoryDetails(null);
        }
      } catch (error) {
        console.error('Error fetching category details:', error);
        setCategoryDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryDetails();
  }, [isOpen, profile, organizationId, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-30" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Credit Risk Profile: {profile || 'N/A'}</h3>
        </div>
        <div className="px-6 py-4 max-h-96 overflow-auto space-y-6">
          {/* Reasonings Section */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Reasonings</h4>
            {reasonings && reasonings.length > 0 ? (
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
                {reasonings.map((r, idx) => (
                  <li key={idx}>{r}</li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500">No reasonings available.</div>
            )}
          </div>

          {/* Dunning Ladder Section */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Dunning Ladder</h4>
            {loading ? (
              <div className="text-sm text-gray-500">Loading dunning ladder...</div>
            ) : (() => {
              if (!categoryDetails || !categoryDetails.entries) {
                return <div className="text-sm text-gray-500">No dunning ladder defined.</div>;
              }

              // Find all dunning ladder entries (dunning_ladder and dunning_ladder.*)
              const dunningLadderEntries = categoryDetails.entries.filter((entry: any) => 
                (entry.key === 'dunning_ladder' || entry.key.startsWith('dunning_ladder.')) && entry.is_active !== false
              );
              
              if (dunningLadderEntries && dunningLadderEntries.length > 0) {
                // Sort entries by the day part (d+0, d+3, d+7, etc.)
                const sortedEntries = dunningLadderEntries.sort((a: any, b: any) => {
                  // Extract day part for sorting
                  const getDayPart = (key: string) => {
                    if (key === 'dunning_ladder') return 'd+0'; // Base entry
                    const dayPart = key.replace('dunning_ladder.', '');
                    return dayPart;
                  };
                  
                  const aDay = getDayPart(a.key);
                  const bDay = getDayPart(b.key);
                  
                  // Sort by day number
                  const aNum = parseInt(aDay.replace('d+', '').split('_')[0]) || 0;
                  const bNum = parseInt(bDay.replace('d+', '').split('_')[0]) || 0;
                  
                  return aNum - bNum;
                });
                
                return (
                  <div className="space-y-2 text-sm text-gray-700">
                    {sortedEntries.map((entry: any, idx: number) => {
                      // Extract the day part from the key and convert to uppercase D
                      const dayPart = entry.key === 'dunning_ladder' ? 'D+0' : entry.key.replace('dunning_ladder.', '').replace('d+', 'D+');
                      
                      return (
                        <div key={idx} className="flex items-start">
                          <span className="font-medium text-gray-900 w-16 mr-3">{dayPart}:</span>
                          <span className="text-gray-700 flex-1">{entry.value}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              } else {
                return (
                  <div className="text-sm text-gray-500">No dunning ladder defined.</div>
                );
              }
            })()}
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RiskProfileModal;
